import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LotusIcon from "@/components/icons/LotusIcon";
import { 
  Heart, Star, Infinity, ChevronRight, ChevronDown, 
  Lock, Smartphone, Leaf, Check, Users, BookOpen,
  Sprout, TreeDeciduous, Flower2, MessageCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import harmonyAppPreview from "@/assets/harmony-app-preview.png";

// App links
const LINKS = {
  app: "https://app.siwongalaxy.com",
  appAnnual: "https://app.siwongalaxy.com",
  whatsapp: "https://wa.me/917042106100",
  membership: "/membership-programs"
};

// Module data
const modules = [
  {
    id: "relationship",
    icon: Heart,
    title: "Relationship Harmony Module",
    tagline: "For couples, singles, and anyone navigating the complexity of human connection.",
    features: [
      "Relationship 3A Assessment — kosha-by-kosha",
      "Pattern recognition across all 5 Koshas",
      "Blame-game to Wonderment transformation tracker",
      "Bhagavad Gita wisdom for relationship challenges",
      "Monthly relationship health pulse check"
    ],
    audience: "Couples rebuilding trust · Singles breaking patterns · Professionals navigating workplace relationships",
    priceMonthly: "₹999",
    priceQuarterly: "₹2,499",
    priceAnnual: "₹9,999",
    savings: "Save 2 months",
    cta: "Start Relationship Module",
    highlight: false
  },
  {
    id: "life",
    icon: Star,
    title: "Life Transformation Module",
    tagline: "For professionals and seekers navigating burnout, purpose, and the gap between where you are and where you belong.",
    features: [
      "Life 3A Assessment — all five dimensions",
      "Burnout-to-vitality",
      "Guna analysis: Tamas, Rajas, or Sattva?",
      "Dharma alignment map — Life purpose and Rhythm",
      "Monthly life transformation pulse check"
    ],
    audience: "Working professionals · Students at crossroads · Anyone seeking purposeful, wisdom-aligned living",
    priceMonthly: "₹999",
    priceQuarterly: "₹2,499",
    priceAnnual: "₹9,999",
    savings: "Save 2 months",
    cta: "Start Life Module",
    highlight: false
  },
  {
    id: "bundle",
    icon: Infinity,
    title: "Complete Harmony Bundle",
    tagline: "The full Siwon experience — relationships and life, woven together as one journey.",
    features: [
      "Everything in both modules",
      "Cross-dimensional insights (how relationships affect career)",
      "Integrated 3A dashboard — one view of your whole life",
      "Priority access to new tools as they launch",
      "The complete path: discord to harmony, stagnation to wonder"
    ],
    audience: "Those ready for deep, whole-life transformation · Serious seekers · Anyone who refuses to compartmentalise",
    priceMonthly: "₹1,699",
    priceQuarterly: "₹4,249",
    priceAnnual: "₹16,999",
    savings: "Save ₹3,389/year",
    cta: "Start Complete Journey",
    highlight: true
  }
];

// Journey stages
const journeyStages = [
  {
    icon: Sprout,
    stage: "Stage 1",
    title: "Rooted",
    emoji: "🌱",
    description: "Your kosha imbalances are mapped. Targeted micro-practices rooted in Vedantic wisdom begin. You know your starting point."
  },
  {
    icon: Leaf,
    stage: "Stage 2",
    title: "Growing",
    emoji: "🌿",
    description: "Monthly check-ins show movement. Old patterns are named. Bhagavad Gita wisdom starts to feel personal, not philosophical."
  },
  {
    icon: TreeDeciduous,
    stage: "Stage 3",
    title: "Deepening",
    emoji: "🌳",
    description: "The Anandamaya kosha stirs. Silence becomes a tool. You witness your life rather than react to it."
  },
  {
    icon: Flower2,
    stage: "Stage 4",
    title: "Blooming",
    emoji: "🌸",
    description: "Silence and Wonderment become lived experience. Relationships heal. Work flows. Life unfolds with purpose."
  }
];

// FAQs
const faqs = [
  {
    question: "Is the app different from the free quiz?",
    answer: "Yes — meaningfully so. The quiz is a one-time diagnostic snapshot across 25 questions. The app is an ongoing journey tool with multiple evolving assessments, progress tracking across your 3A stages, and personalised Vedic solution pathways that update as you grow. Think of the quiz as the door. The app is what's inside."
  },
  {
    question: "Do I need a membership to use the app?",
    answer: "No. The app is a completely separate subscription. You can use the app without any membership. Membership adds the community layer — live webinars with Vinod, the LMS course library, and peer connection. Many people start with the app alone and add membership later when they're ready for more."
  },
  {
    question: "Which module should I start with?",
    answer: "If your primary pain point is your relationship — with your partner, family, or colleagues — start with the Relationship Module. If your primary pain is burnout, lack of purpose, or life feeling stuck — start with the Life Module. If both resonate, the Complete Bundle gives you an integrated view that neither module alone can."
  },
  {
    question: "Can I cancel anytime?",
    answer: "Monthly plans can be cancelled at any time. Annual plans are a 12-month commitment from the date of purchase. We recommend starting monthly if you're unsure, then switching to annual once you've experienced the app's value."
  },
  {
    question: "Is my data private?",
    answer: "Completely. Your quiz responses, assessment results, and personal journey data are private to your account. Vinod and the Siwon team do not share or sell your personal data. Your inner journey is yours alone."
  }
];

// FAQ Accordion Item
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex justify-between items-center text-left"
      >
        <span className="font-semibold text-foreground pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-primary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="pb-5 text-muted-foreground leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const HarmonyApp = () => {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" data-testid="harmony-app-page">
      <Header />
      
      {/* SECTION 1: Hero Banner */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-primary via-teal-dark to-teal-dark mandala-bg relative overflow-hidden">
        <div className="absolute inset-0 mandala-bg opacity-30" />
        
        <div className="container relative z-10 px-4">
          <div className="grid lg:grid-cols-12 gap-4 items-center">
            
            {/* Left App Screenshot */}
            <div className="hidden lg:flex lg:col-span-3 flex-col justify-center items-center">
              <div className="relative">
                {/* App screenshot with shadow */}
                <div className="relative shadow-2xl" style={{ width: '200px' }}>
                  <img 
                    src={harmonyAppPreview} 
                    alt="Siwon Harmony App" 
                    className="w-full h-auto block rounded-2xl"
                  />
                  {/* Reflection/shine effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none"></div>
                </div>
                {/* Subtle glow */}
                <div className="absolute -inset-4 bg-purple-500/20 blur-2xl rounded-full -z-10"></div>
              </div>
              {/* CTA Button below left screenshot */}
              <a
                href={LINKS.app}
                className="btn-lime inline-flex items-center justify-center gap-2 mt-6"
                data-testid="hero-start-journey-left-btn"
              >
                Start Your Journey
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
            
            {/* Center Content */}
            <div className="lg:col-span-6 text-center animate-fade-up">
              <p className="text-accent text-sm font-medium mb-3">
                By Vinod Kumar · Relationship and Life WISDOM Coach
              </p>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4 leading-tight">
                Your 3A Journey —<br />
                <span className="text-accent italic">Deeper. Daily.</span><br />
                Guided by Vedic Wisdom.
              </h1>
              <p className="text-base md:text-lg text-primary-foreground/85 max-w-xl mx-auto mb-6 leading-relaxed">
                The quiz showed you where you are.<br />
                The Siwon Harmony App walks you through where to go.
              </p>
              
              {/* See Pricing Button - Centered */}
              <div className="flex justify-center mb-6">
                <button
                  onClick={scrollToPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/50 text-white font-semibold hover:bg-white/10 transition-all"
                >
                  See Pricing
                  <ChevronDown className="w-5 h-5" />
                </button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-3 text-xs md:text-sm text-primary-foreground/70">
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3 md:w-4 md:h-4" />
                  Secure · Private
                </span>
                <span className="flex items-center gap-1">
                  <Smartphone className="w-3 h-3 md:w-4 md:h-4" />
                  Any device · No download
                </span>
                <span className="flex items-center gap-1">
                  <Leaf className="w-3 h-3 md:w-4 md:h-4" />
                  Vedantic wisdom
                </span>
              </div>
              
              {/* Mobile App Preview - only shown on mobile/tablet */}
              <div className="mt-8 flex flex-col items-center lg:hidden">
                <div className="relative shadow-2xl" style={{ width: '160px' }}>
                  <img 
                    src={harmonyAppPreview} 
                    alt="Siwon Harmony App Preview" 
                    className="w-full h-auto block rounded-xl"
                  />
                </div>
                {/* CTA for mobile */}
                <a
                  href={LINKS.app}
                  className="btn-lime inline-flex items-center justify-center gap-2 mt-4"
                  data-testid="hero-start-journey-mobile-btn"
                >
                  Start Your Journey
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            {/* Right App Screenshot */}
            <div className="hidden lg:flex lg:col-span-3 flex-col justify-center items-center">
              <div className="relative">
                {/* App screenshot with shadow */}
                <div className="relative shadow-2xl" style={{ width: '200px' }}>
                  <img 
                    src={harmonyAppPreview} 
                    alt="Siwon Harmony App" 
                    className="w-full h-auto block rounded-2xl"
                  />
                  {/* Reflection/shine effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none"></div>
                </div>
                {/* Subtle glow */}
                <div className="absolute -inset-4 bg-purple-500/20 blur-2xl rounded-full -z-10"></div>
              </div>
              {/* CTA Button below right screenshot */}
              <a
                href={LINKS.app}
                className="btn-lime inline-flex items-center justify-center gap-2 mt-6"
                data-testid="hero-start-journey-right-btn"
              >
                Start Your Journey
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
            
          </div>
        </div>
      </section>

      {/* SECTION 2: The Bridge (Quiz vs App) */}
      <section className="py-12 bg-background">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              The Quiz is a Glimpse.<br />
              <span className="text-accent">The App is the Full Mirror.</span>
            </h2>
            <p className="text-muted-foreground">
              One shows you the door. The other walks you through it.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Quiz Card */}
            <Card className="border-2 border-orange-200 bg-orange-50/50">
              <CardHeader>
                <CardTitle className="text-xl text-orange-800">Free Diagnostic Quiz</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    One-time snapshot of your patterns
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    25 questions across 5 Koshas
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    Single PDF result report
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    Shows you WHERE you are today
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-500 mt-1">•</span>
                    <em>Great starting point</em>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            {/* App Card */}
            <Card className="border-2 border-primary bg-primary/5 relative">
              <div className="absolute -top-3 right-4 bg-accent text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                ✦ Recommended
              </div>
              <CardHeader>
                <CardTitle className="text-xl text-primary">Siwon Harmony App</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    Ongoing 3A journey tracker — month by month
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    Deep multi-dimensional assessments
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    Personalised Vedic solutions for your patterns
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    Tracks your movement: Rooted → Blooming
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    Monthly new insights as you evolve
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Quote */}
          <div className="text-center mt-12 max-w-2xl mx-auto">
            <blockquote className="font-serif text-lg italic text-muted-foreground">
              "Most people take the quiz and feel seen for the first time.
              <br />The app is where they do something about it."
            </blockquote>
            <p className="mt-4 font-semibold text-primary">— Vinod Kumar</p>
          </div>
        </div>
      </section>

      {/* SECTION 3: Modules */}
      <section className="py-12 bg-sage-light">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              Choose Your Path
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Two focused modules. One integrated journey. You decide where to begin.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
            {modules.map((module) => {
              const IconComponent = module.icon;
              return (
                <Card 
                  key={module.id} 
                  className={`relative flex flex-col ${module.highlight ? 'border-2 border-primary shadow-lg' : 'border border-border'}`}
                  data-testid={`module-card-${module.id}`}
                >
                  {module.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                      ✦ Best Value
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${module.highlight ? 'bg-primary/20' : 'bg-muted'}`}>
                      <IconComponent className={`w-8 h-8 ${module.highlight ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <CardTitle className="text-xl">{module.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-2">{module.tagline}</p>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-grow">
                    <ul className="space-y-2 mb-6 flex-grow">
                      {module.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="text-center border-t border-border pt-6 mt-auto">
                      <p className="text-2xl font-bold text-primary">
                        {module.priceMonthly}
                        <span className="text-sm font-normal text-muted-foreground"> + GST / month</span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Quarterly: <span className="font-semibold text-foreground">{module.priceQuarterly}</span> + GST
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Annual: <span className="font-semibold text-foreground">{module.priceAnnual}</span> + GST{" "}
                        <span className="text-accent font-medium">{module.savings}</span>
                      </p>
                      <a
                        href={LINKS.app}
                        className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                          module.highlight 
                            ? 'btn-lime' 
                            : 'bg-primary text-primary-foreground hover:bg-primary/90'
                        }`}
                      >
                        {module.cta}
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <p className="text-center text-sm text-muted-foreground mt-8">
            All prices exclude 18% GST · Cancel monthly plan anytime · Quarterly plans run 3 months · Annual plans run 12 months
          </p>
        </div>
      </section>

      {/* SECTION 4: Pricing Table */}
      <section id="pricing" className="py-12 bg-background">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              Simple, Transparent Pricing<span className="text-accent">*</span>
            </h2>
            <p className="text-muted-foreground">
              No hidden fees. No confusion. Just choose what works for you.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-primary">
                  <th className="p-4 text-left"></th>
                  <th className="p-4 text-center font-semibold text-primary">Relationship Module</th>
                  <th className="p-4 text-center font-semibold text-primary">Life Coaching Module</th>
                  <th className="p-4 text-center font-semibold text-primary bg-primary/5">Complete Bundle</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-4 font-medium">Monthly</td>
                  <td className="p-4 text-center">₹999 + GST</td>
                  <td className="p-4 text-center">₹999 + GST</td>
                  <td className="p-4 text-center bg-primary/5 font-semibold">₹1,699 + GST</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-4 font-medium">Quarterly</td>
                  <td className="p-4 text-center">₹2,499 + GST</td>
                  <td className="p-4 text-center">₹2,499 + GST</td>
                  <td className="p-4 text-center bg-primary/5 font-semibold">₹4,249 + GST</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-4 font-medium">Annual</td>
                  <td className="p-4 text-center">₹9,999 + GST</td>
                  <td className="p-4 text-center">₹9,999 + GST</td>
                  <td className="p-4 text-center bg-primary/5 font-semibold">₹16,999 + GST</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-4 font-medium">Effective monthly</td>
                  <td className="p-4 text-center text-muted-foreground">₹833/month</td>
                  <td className="p-4 text-center text-muted-foreground">₹833/month</td>
                  <td className="p-4 text-center bg-primary/5 text-muted-foreground">₹1,416/month</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Annual saving</td>
                  <td className="p-4 text-center text-accent font-medium">₹1,989</td>
                  <td className="p-4 text-center text-accent font-medium">₹1,989</td>
                  <td className="p-4 text-center bg-primary/5 text-accent font-semibold">₹3,389</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a
              href={LINKS.app}
              className="btn-lime inline-flex items-center justify-center gap-2"
              data-testid="pricing-start-monthly-btn"
            >
              Open the App — Start Monthly
            </a>
            <a
              href={LINKS.appAnnual}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-primary text-primary font-semibold hover:bg-primary/5 transition-all"
            >
              Go Annual — Save More ✦
            </a>
          </div>
          
          <p className="text-center text-sm text-muted-foreground mt-6 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />
            Secure payment · Your data is private · Cancel monthly anytime
          </p>
          
          {/* Pricing Disclaimer */}
          <p className="text-center text-sm text-muted-foreground mt-4 max-w-2xl mx-auto font-semibold">
            *Pricing is subject to change due to upgradation and festive/other offers from time to time. Check actual App page for updated pricing.
          </p>
        </div>
      </section>

      {/* SECTION 5: App + Membership */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              The App and Membership Work Together —<br />
              <span className="text-accent">But You Don't Need Both to Begin.</span>
            </h2>
            <p className="text-primary-foreground/80">
              Start where you are. Add more when you're ready.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* App Only */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
              <h3 className="font-serif text-xl font-semibold mb-4">App Only — Start Here</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  Self-guided 3A exploration at your own pace
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  Structured assessments without live sessions
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  Flexibility — monthly or annual
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  A private, personal transformation tool
                </li>
              </ul>
              <p className="italic text-primary-foreground/70 text-sm">
                "Perfect for those who learn by doing."
              </p>
            </div>
            
            {/* App + Membership */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
              <h3 className="font-serif text-xl font-semibold mb-4">App + Membership — Go Deeper</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  Live group webinars with Vinod (1–3/month)
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  LMS course library on TagMango
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  Community of fellow seekers and practitioners
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-5 h-5 text-accent mt-0.5" />
                  Guided transformation within a sangha
                </li>
              </ul>
              <p className="italic text-primary-foreground/70 text-sm">
                "The app is your daily practice. Membership is your sangha."
              </p>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <a
              href={LINKS.app}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-semibold hover:bg-white/10 transition-all"
            >
              Get the App Only
              <ChevronRight className="w-5 h-5" />
            </a>
            <Link
              to={LINKS.membership}
              className="btn-lime inline-flex items-center justify-center gap-2"
            >
              See Membership Plans
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 6: Journey Stages */}
      <section className="py-12 bg-sage-light">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              What Your Journey Looks Like Inside the App
            </h2>
            <p className="text-muted-foreground">
              Four stages. One continuous transformation.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {journeyStages.map((stage, idx) => (
              <Card key={idx} className="text-center" data-testid={`journey-stage-${idx + 1}`}>
                <CardContent className="pt-8">
                  <div className="text-4xl mb-4">{stage.emoji}</div>
                  <p className="text-sm text-accent font-medium mb-1">{stage.stage}</p>
                  <h3 className="font-serif text-xl font-semibold text-primary mb-3">{stage.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {stage.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: FAQs */}
      <section className="py-12 bg-background">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              Questions? Honest Answers.
            </h2>
            <p className="text-muted-foreground">
              Everything you need to know before you begin.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, idx) => (
              <FAQItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: Final CTA */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
              Your Harmony Journey Starts<br />
              <span className="text-accent">With One Decision.</span>
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              You've already taken the quiz. You already know something is ready to shift.
              The Siwon Harmony App is where that shift becomes a daily practice.
            </p>
            
            {/* Quote */}
            <blockquote className="font-serif text-lg italic text-primary-foreground/80 mb-6 border-l-4 border-accent pl-6 text-left max-w-2xl mx-auto">
              "Most people wait for the perfect moment to begin their inner work.
              The Vedas have a different view — the moment you feel the pull, you are already being called.
              Begin now. The rest unfolds."
            </blockquote>
            <p className="font-semibold text-accent mb-10">
              — Vinod Kumar, Founder · Siwon Galaxy
            </p>
            
            {/* Final CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={LINKS.app}
                className="btn-lime inline-flex items-center justify-center gap-2 text-lg px-10 py-5"
                data-testid="final-cta-open-app-btn"
              >
                Open the App
                <ChevronRight className="w-5 h-5" />
              </a>
              <a
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-semibold hover:bg-white/10 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Talk to Us First
              </a>
            </div>
            
            <p className="text-sm text-primary-foreground/60 mt-8">
              Questions? WhatsApp +91 7042106100 · siwongalaxy108@gmail.com
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HarmonyApp;
